import { createFileRoute } from '@tanstack/react-router'
import { useCallback, useEffect, useState } from 'react'
import {
  RefreshCwIcon,
  CalendarIcon,
  CheckCircleIcon,
  AlertTriangleIcon,
  UsersIcon,
  FileSpreadsheetIcon,
  PlayIcon,
  ShieldAlertIcon,
  HistoryIcon,
  MessageSquarePlusIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  PlusIcon,
  PencilIcon,
  BanIcon,
} from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  getTaxGenerationStatusApi,
  generateTaxFormsApi,
  getOrgTaxDeliveryApi,
  addOrgTaxNoteApi,
  getCountriesApi,
  listTaxFormTypesApi,
  createTaxFormTypeApi,
  updateTaxFormTypeApi,
  deleteTaxFormTypeApi,
  type Country,
  type TaxFormType,
} from '@/lib/api'
import { Dialog } from '@/components/ui/dialog'
import { Select } from '@/components/ui/select'
import { BRAND_MINT } from '@/lib/utils'

interface OrgStatus {
  org_id: number
  org_name: string | null
  employees: number
  generated: number
  pending: number
}

/** Empty form-state factory for the create/edit dialog. */
function emptyFormTypeState() {
  return {
    country_id: '' as number | '',
    form_code: '',
    form_name: '',
    description: '',
    applies_to: 'both' as TaxFormType['applies_to'],
    requires_tax_id: true,
    filing_frequency: 'annual' as TaxFormType['filing_frequency'],
    filing_month: '',
    is_active: true,
  }
}

interface DeliveryEntry {
  delivery_log_id: number
  tax_year: number
  action: 'downloaded' | 'note'
  actor_role: string | null
  note: string | null
  created_at: string
}

function formatTimestamp(iso: string): string {
  try {
    return new Date(iso).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return iso
  }
}

export const Route = createFileRoute('/dashboard/admin/tax-forms')({
  component: () => {
    const currentYear = new Date().getFullYear()
    const [year, setYear] = useState(currentYear)
    const [orgs, setOrgs] = useState<OrgStatus[]>([])
    const [totals, setTotals] = useState<{ employees: number; generated: number; pending: number } | null>(null)
    const [loading, setLoading] = useState(true)
    const [generating, setGenerating] = useState(false)
    const [confirmGenerate, setConfirmGenerate] = useState(false)

    // Per-org delivery audit panel
    const [auditOrg, setAuditOrg] = useState<OrgStatus | null>(null)
    const [auditRows, setAuditRows] = useState<DeliveryEntry[]>([])
    const [auditLoading, setAuditLoading] = useState(false)
    const [noteText, setNoteText] = useState('')
    const [noteSaving, setNoteSaving] = useState(false)

    // ── Form types CRUD (super-admin) ──
    const [formTypes, setFormTypes] = useState<TaxFormType[]>([])
    const [formTypesLoading, setFormTypesLoading] = useState(false)
    const [countries, setCountries] = useState<Country[]>([])
    const [formDialogOpen, setFormDialogOpen] = useState(false)
    const [formEditingId, setFormEditingId] = useState<number | null>(null)
    const [formState, setFormState] = useState(emptyFormTypeState())
    const [formSaving, setFormSaving] = useState(false)

    const fetchFormTypes = useCallback(async () => {
      setFormTypesLoading(true)
      try {
        const res = await listTaxFormTypesApi()
        setFormTypes(res.data)
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to load tax form types')
      } finally {
        setFormTypesLoading(false)
      }
    }, [])

    useEffect(() => {
      fetchFormTypes()
      getCountriesApi()
        .then((res) => setCountries(res.data))
        .catch(() => toast.error('Could not load countries'))
    }, [fetchFormTypes])

    const openCreateForm = () => {
      setFormEditingId(null)
      setFormState(emptyFormTypeState())
      setFormDialogOpen(true)
    }

    const openEditForm = (t: TaxFormType) => {
      setFormEditingId(t.tax_form_type_id)
      setFormState({
        country_id: t.country_id,
        form_code: t.form_code,
        form_name: t.form_name,
        description: t.description ?? '',
        applies_to: t.applies_to,
        requires_tax_id: t.requires_tax_id,
        filing_frequency: t.filing_frequency,
        filing_month: t.filing_month != null ? String(t.filing_month) : '',
        is_active: t.is_active,
      })
      setFormDialogOpen(true)
    }

    const handleSaveForm = async () => {
      if (formState.country_id === '' || !formState.form_code.trim() || !formState.form_name.trim()) {
        toast.error('Country, form code and form name are required')
        return
      }
      const payload = {
        country_id: formState.country_id as number,
        form_code: formState.form_code.trim(),
        form_name: formState.form_name.trim(),
        description: formState.description.trim() || undefined,
        applies_to: formState.applies_to,
        requires_tax_id: formState.requires_tax_id,
        filing_frequency: formState.filing_frequency,
        filing_month: formState.filing_month === '' ? null : Number(formState.filing_month),
        is_active: formState.is_active,
      }
      setFormSaving(true)
      try {
        if (formEditingId == null) {
          await createTaxFormTypeApi(payload)
          toast.success('Tax form type created')
        } else {
          await updateTaxFormTypeApi(formEditingId, payload)
          toast.success('Tax form type updated')
        }
        setFormDialogOpen(false)
        fetchFormTypes()
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to save tax form type')
      } finally {
        setFormSaving(false)
      }
    }

    const handleDeactivateForm = async (t: TaxFormType) => {
      if (!window.confirm(`Deactivate ${t.form_code} — ${t.form_name}? Historical records stay valid.`)) return
      try {
        await deleteTaxFormTypeApi(t.tax_form_type_id)
        toast.success(`${t.form_code} deactivated`)
        fetchFormTypes()
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to deactivate tax form type')
      }
    }

    const fetchStatus = useCallback(
      async (signal?: { cancelled: boolean }) => {
        setLoading(true)
        try {
          const res = await getTaxGenerationStatusApi(year)
          if (signal?.cancelled) return
          setTotals(res.totals)
          setOrgs(res.organizations)
        } catch (err) {
          if (signal?.cancelled) return
          toast.error(err instanceof Error ? err.message : 'Failed to load generation status')
        } finally {
          if (!signal?.cancelled) setLoading(false)
        }
      },
      [year],
    )

    useEffect(() => {
      const signal = { cancelled: false }
      fetchStatus(signal)
      return () => {
        signal.cancelled = true
      }
    }, [fetchStatus])

    const handleGenerate = async () => {
      setGenerating(true)
      try {
        const res = await generateTaxFormsApi(year)
        const s = res.summary
        toast.success(
          `${res.message} — ${s?.generated ?? 0} generated, ${s?.updated ?? 0} updated, ${s?.skipped ?? 0} skipped`,
        )
        setConfirmGenerate(false)
        fetchStatus()
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Generation failed')
      } finally {
        setGenerating(false)
      }
    }

    const openAudit = async (org: OrgStatus) => {
      setAuditOrg(org)
      setAuditRows([])
      setNoteText('')
      setAuditLoading(true)
      try {
        const res = await getOrgTaxDeliveryApi(org.org_id, year)
        setAuditRows(res.data)
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to load delivery audit')
      } finally {
        setAuditLoading(false)
      }
    }

    const handleAddNote = async () => {
      if (!auditOrg || !noteText.trim()) return
      setNoteSaving(true)
      try {
        await addOrgTaxNoteApi(auditOrg.org_id, year, noteText.trim())
        toast.success('Escalation note recorded')
        setNoteText('')
        openAudit(auditOrg)
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to record note')
      } finally {
        setNoteSaving(false)
      }
    }

    const generatedPct = totals && totals.employees > 0 ? Math.round((totals.generated / totals.employees) * 100) : 0

    return (
      <div className="flex flex-col gap-6">
        {/* Header */}
        <div className="flex flex-col gap-1">
          <h1 className="font-['Source_Serif_4',ui-serif,Georgia,serif] text-2xl font-semibold text-foreground">
            Tax Form Supervision
          </h1>
          <p className="text-sm text-muted-foreground">
            Annual tax-form generation and delivery supervision. PeerPass provides tax data packages to
            organizations for their own employee tax declaration.
          </p>
        </div>

        {/* Year + actions */}
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-muted-foreground">
            <CalendarIcon className="size-4" />
            Tax year
          </label>
          <select
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="h-10 rounded-md border border-border bg-background px-3 text-sm text-foreground focus-visible:border-[#00E5A0] focus-visible:outline-none"
          >
            {[currentYear, currentYear - 1, currentYear - 2].map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>

          <div className="flex-1" />

          <Button
            variant="outline"
            onClick={() => fetchStatus()}
            className="text-muted-foreground hover:text-foreground"
          >
            <RefreshCwIcon className="size-4" />
            Refresh
          </Button>

          {confirmGenerate ? (
            <div className="flex items-center gap-2">
              <span className="text-xs text-amber-600/90 dark:text-amber-400/90">Regenerate {year} forms?</span>
              <Button
                onClick={handleGenerate}
                disabled={generating}
                className="bg-amber-500 text-[#0B1120] hover:bg-amber-500/90 font-semibold"
              >
                <PlayIcon className="size-4" />
                {generating ? 'Generating…' : 'Confirm'}
              </Button>
              <Button
                variant="outline"
                onClick={() => setConfirmGenerate(false)}
                disabled={generating}
                className="text-muted-foreground hover:text-foreground"
              >
                Cancel
              </Button>
            </div>
          ) : (
            <Button
              onClick={() => setConfirmGenerate(true)}
              disabled={loading || generating}
              className="bg-[#00E5A0] text-[#0B1120] hover:bg-[#00E5A0]/90 font-semibold"
            >
              <PlayIcon className="size-4" />
              Generate for {year}
            </Button>
          )}
        </div>

        {/* Totals */}
        {totals && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="rounded-xl border border-border bg-card p-4">
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <UsersIcon className="size-3.5" /> Employees
                </span>
                <span className="text-lg font-semibold text-foreground">{totals.employees}</span>
              </div>
            </div>
            <div className="rounded-xl border border-[#00E5A0]/20 bg-[#00E5A0]/[0.05] p-4">
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-1.5 text-xs text-emerald-600/80 dark:text-emerald-400/80">
                  <CheckCircleIcon className="size-3.5" /> Generated
                </span>
                <span className="text-lg font-semibold text-emerald-600 dark:text-emerald-400">{totals.generated}</span>
              </div>
            </div>
            <div className="rounded-xl border border-amber-500/20 bg-amber-500/[0.05] p-4">
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-1.5 text-xs text-amber-600/80 dark:text-amber-400/80">
                  <AlertTriangleIcon className="size-3.5" /> Pending
                </span>
                <span className="text-lg font-semibold text-amber-600 dark:text-amber-400">{totals.pending}</span>
              </div>
            </div>
          </div>
        )}

        {/* Generation progress */}
        {totals && totals.employees > 0 && (
          <div className="rounded-xl border border-border bg-card p-4">
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="text-muted-foreground">Generation coverage — {year}</span>
              <span className="text-foreground font-medium">{generatedPct}%</span>
            </div>
            <div className="h-2 rounded-full bg-muted overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{ width: `${generatedPct}%`, backgroundColor: BRAND_MINT }}
              />
            </div>
          </div>
        )}

        {/* Per-org table */}
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <div className="flex items-center gap-2">
              <FileSpreadsheetIcon className="size-4 text-emerald-600 dark:text-emerald-400" />
              <h2 className="text-sm font-semibold text-foreground">Organizations — {year}</h2>
            </div>
            <span className="text-xs text-muted-foreground/70">{orgs.length} org{orgs.length === 1 ? '' : 's'}</span>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <span className="size-6 rounded-full border-2 border-border border-t-emerald-600 dark:border-t-emerald-400 animate-spin" />
            </div>
          ) : orgs.length === 0 ? (
            <div className="py-12 text-center">
              <p className="text-sm text-muted-foreground">No organizations with employees.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-muted-foreground/70 border-b border-border">
                    <th className="px-4 py-2.5 font-medium">Organization</th>
                    <th className="px-4 py-2.5 font-medium text-right">Employees</th>
                    <th className="px-4 py-2.5 font-medium text-right">Generated</th>
                    <th className="px-4 py-2.5 font-medium text-right">Pending</th>
                    <th className="px-4 py-2.5 font-medium text-right">Delivery audit</th>
                  </tr>
                </thead>
                <tbody>
                  {orgs.map((o) => {
                    const isOpen = auditOrg?.org_id === o.org_id
                    return (
                      <OrgRow
                        key={o.org_id}
                        org={o}
                        isOpen={isOpen}
                        onToggle={() => (isOpen ? setAuditOrg(null) : openAudit(o))}
                      />
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Delivery audit panel */}
        {auditOrg && (
          <div className="rounded-xl border border-border bg-card overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <div className="flex items-center gap-2">
                <HistoryIcon className="size-4 text-emerald-600 dark:text-emerald-400" />
                <h2 className="text-sm font-semibold text-foreground">
                  Delivery audit — {auditOrg.org_name ?? `Org #${auditOrg.org_id}`}
                </h2>
              </div>
              <span className="text-xs text-muted-foreground/70">{year}</span>
            </div>

            <div className="p-4">
              {auditLoading ? (
                <div className="flex justify-center py-8">
                  <span className="size-6 rounded-full border-2 border-border border-t-emerald-600 dark:border-t-emerald-400 animate-spin" />
                </div>
              ) : auditRows.length === 0 ? (
                <p className="py-6 text-center text-sm text-muted-foreground">
                  No delivery or escalation activity recorded for {year}.
                </p>
              ) : (
                <ul className="flex flex-col gap-2">
                  {auditRows.map((row) => (
                    <li
                      key={row.delivery_log_id}
                      className="flex items-start gap-3 rounded-lg border border-border bg-muted/40 px-3 py-2.5"
                    >
                      <span
                        className={`mt-0.5 inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-xs ${
                          row.action === 'downloaded'
                            ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                            : 'bg-amber-500/10 text-amber-600 dark:text-amber-400'
                        }`}
                      >
                        {row.action === 'downloaded' ? 'Downloaded' : 'Escalation note'}
                      </span>
                      <div className="flex flex-col gap-0.5 min-w-0">
                        {row.note && (
                          <p className="text-sm text-foreground/80 break-words">{row.note}</p>
                        )}
                        <p className="text-xs text-muted-foreground/70">
                          {row.actor_role ? `${row.actor_role} · ` : ''}
                          {formatTimestamp(row.created_at)}
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>
              )}

              {/* Escalation note */}
              <div className="mt-4 flex flex-col gap-2 border-t border-border pt-4">
                <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <MessageSquarePlusIcon className="size-3.5" /> Record escalation note
                </label>
                <div className="flex flex-col sm:flex-row gap-2">
                  <textarea
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                    rows={2}
                    placeholder="e.g. Awaiting tax-ID confirmation from org before generation…"
                    className="flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus-visible:border-[#00E5A0] focus-visible:outline-none"
                  />
                  <Button
                    onClick={handleAddNote}
                    disabled={noteSaving || !noteText.trim()}
                    variant="outline"
                    className="text-muted-foreground hover:text-foreground"
                  >
                    {noteSaving ? 'Saving…' : 'Add note'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Form types CRUD (super-admin) */}
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <div className="flex items-center gap-2">
              <FileSpreadsheetIcon className="size-4 text-emerald-600 dark:text-emerald-400" />
              <h2 className="text-sm font-semibold text-foreground">Tax form types</h2>
            </div>
            <Button
              onClick={openCreateForm}
              className="bg-[#00E5A0] text-[#0B1120] hover:bg-[#00E5A0]/90 font-semibold"
            >
              <PlusIcon className="size-4" />
              New form type
            </Button>
          </div>

          {formTypesLoading ? (
            <div className="flex justify-center py-10">
              <span className="size-6 rounded-full border-2 border-border border-t-emerald-600 dark:border-t-emerald-400 animate-spin" />
            </div>
          ) : formTypes.length === 0 ? (
            <div className="py-10 text-center">
              <p className="text-sm text-muted-foreground">No tax form types defined yet.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-muted-foreground/70 border-b border-border">
                    <th className="px-4 py-2.5 font-medium">Country</th>
                    <th className="px-4 py-2.5 font-medium">Code</th>
                    <th className="px-4 py-2.5 font-medium">Form</th>
                    <th className="px-4 py-2.5 font-medium">Applies to</th>
                    <th className="px-4 py-2.5 font-medium">Frequency</th>
                    <th className="px-4 py-2.5 font-medium">Status</th>
                    <th className="px-4 py-2.5 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {formTypes.map((t) => (
                    <tr key={t.tax_form_type_id} className="border-b border-border/60 last:border-0 hover:bg-muted/40">
                      <td className="px-4 py-2.5 text-foreground/80">{t.country_name}</td>
                      <td className="px-4 py-2.5 font-mono text-emerald-600 dark:text-emerald-400">{t.form_code}</td>
                      <td className="px-4 py-2.5 text-foreground max-w-[220px] truncate">{t.form_name}</td>
                      <td className="px-4 py-2.5 text-muted-foreground capitalize">{t.applies_to}</td>
                      <td className="px-4 py-2.5 text-muted-foreground capitalize">
                        {t.filing_frequency}
                        {t.filing_month != null ? ` (month ${t.filing_month})` : ''}
                      </td>
                      <td className="px-4 py-2.5">
                        <span
                          className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs ${
                            t.is_active
                              ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                              : 'bg-muted text-muted-foreground'
                          }`}
                        >
                          {t.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-right whitespace-nowrap">
                        <button
                          onClick={() => openEditForm(t)}
                          className="inline-flex items-center gap-1 rounded-md border border-border px-2.5 py-1 text-xs text-muted-foreground hover:border-emerald-500/50 hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors mr-2"
                        >
                          <PencilIcon className="size-3.5" /> Edit
                        </button>
                        {t.is_active && (
                          <button
                            onClick={() => handleDeactivateForm(t)}
                            className="inline-flex items-center gap-1 rounded-md border border-border px-2.5 py-1 text-xs text-muted-foreground hover:border-red-500/50 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                          >
                            <BanIcon className="size-3.5" /> Deactivate
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Form type create/edit dialog */}
        <Dialog
          open={formDialogOpen}
          onClose={() => !formSaving && setFormDialogOpen(false)}
          title={formEditingId == null ? 'New tax form type' : 'Edit tax form type'}
          description="Defines which tax forms exist per country and who they apply to."
        >
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1">
              <label className="text-xs text-muted-foreground">Country</label>
              <Select
                value={formState.country_id}
                onChange={(e) =>
                  setFormState((s) => ({
                    ...s,
                    country_id: e.target.value === '' ? '' : Number(e.target.value),
                  }))
                }
                className="text-foreground"
              >
                <option value="">Select a country</option>
                {countries.map((c) => (
                  <option key={c.country_id} value={c.country_id}>
                    {c.country}
                  </option>
                ))}
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1">
                <label className="text-xs text-muted-foreground">Form code</label>
                <input
                  value={formState.form_code}
                  onChange={(e) => setFormState((s) => ({ ...s, form_code: e.target.value }))}
                  placeholder="e.g. 1099-INT"
                  className="rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus-visible:border-[#00E5A0] focus-visible:outline-none"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs text-muted-foreground">Filing month (1-12)</label>
                <input
                  value={formState.filing_month}
                  onChange={(e) => setFormState((s) => ({ ...s, filing_month: e.target.value }))}
                  placeholder="e.g. 1 for Jan 31"
                  inputMode="numeric"
                  className="rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus-visible:border-[#00E5A0] focus-visible:outline-none"
                />
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-muted-foreground">Form name</label>
              <input
                value={formState.form_name}
                onChange={(e) => setFormState((s) => ({ ...s, form_name: e.target.value }))}
                placeholder="e.g. Interest Income (US)"
                className="rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus-visible:border-[#00E5A0] focus-visible:outline-none"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-muted-foreground">Description</label>
              <textarea
                value={formState.description}
                onChange={(e) => setFormState((s) => ({ ...s, description: e.target.value }))}
                rows={2}
                placeholder="Optional — what this form reports and to whom"
                className="rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus-visible:border-[#00E5A0] focus-visible:outline-none"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1">
                <label className="text-xs text-muted-foreground">Applies to</label>
                <Select
                  value={formState.applies_to}
                  onChange={(e) =>
                    setFormState((s) => ({
                      ...s,
                      applies_to: e.target.value as TaxFormType['applies_to'],
                    }))
                  }
                  className="text-foreground"
                >
                  <option value="lender">Lender</option>
                  <option value="borrower">Borrower</option>
                  <option value="both">Both</option>
                </Select>
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs text-muted-foreground">Filing frequency</label>
                <Select
                  value={formState.filing_frequency}
                  onChange={(e) =>
                    setFormState((s) => ({
                      ...s,
                      filing_frequency: e.target.value as TaxFormType['filing_frequency'],
                    }))
                  }
                  className="text-foreground"
                >
                  <option value="annual">Annual</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="monthly">Monthly</option>
                </Select>
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-foreground">
              <input
                type="checkbox"
                checked={formState.requires_tax_id}
                onChange={(e) => setFormState((s) => ({ ...s, requires_tax_id: e.target.checked }))}
                className="size-4 accent-[#00E5A0]"
              />
              Requires tax ID
            </label>
            <label className="flex items-center gap-2 text-sm text-foreground">
              <input
                type="checkbox"
                checked={formState.is_active}
                onChange={(e) => setFormState((s) => ({ ...s, is_active: e.target.checked }))}
                className="size-4 accent-[#00E5A0]"
              />
              Active
            </label>
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setFormDialogOpen(false)}
              disabled={formSaving}
              className="text-muted-foreground hover:text-foreground"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveForm}
              disabled={formSaving}
              className="bg-[#00E5A0] text-[#0B1120] hover:bg-[#00E5A0]/90 font-semibold"
            >
              {formSaving ? 'Saving…' : formEditingId == null ? 'Create' : 'Save changes'}
            </Button>
          </div>
        </Dialog>

        {/* Supervision disclaimer */}
        <div className="rounded-xl border border-border bg-muted/30 p-4">
          <p className="text-xs text-muted-foreground leading-relaxed">
            <span className="flex items-center gap-1.5 text-muted-foreground font-medium mb-1">
              <ShieldAlertIcon className="size-3.5" /> Supervision note
            </span>
            Generation is idempotent — re-running upserts existing {year} records. The delivery audit trail is
            append-only and records both package downloads and escalation notes for compliance review.
          </p>
        </div>
      </div>
    )
  },
})

function OrgRow({
  org,
  isOpen,
  onToggle,
}: {
  org: OrgStatus
  isOpen: boolean
  onToggle: () => void
}) {
  return (
    <tr className="border-b border-border/60 last:border-0 hover:bg-muted/40">
      <td className="px-4 py-2.5 text-foreground">{org.org_name ?? `Org #${org.org_id}`}</td>
      <td className="px-4 py-2.5 text-right text-foreground/80">{org.employees}</td>
      <td className="px-4 py-2.5 text-right text-emerald-600 dark:text-emerald-400">{org.generated}</td>
      <td className="px-4 py-2.5 text-right text-amber-600 dark:text-amber-400">{org.pending}</td>
      <td className="px-4 py-2.5 text-right">
        <button
          onClick={onToggle}
          className="inline-flex items-center gap-1 rounded-md border border-border px-2.5 py-1 text-xs text-muted-foreground hover:border-emerald-500/50 hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors"
        >
          {isOpen ? <ChevronUpIcon className="size-3.5" /> : <ChevronDownIcon className="size-3.5" />}
          {isOpen ? 'Close' : 'Audit'}
        </button>
      </td>
    </tr>
  )
}
